import { useState, useEffect, useCallback } from 'react';
import {
  Modal, Tabs, Tag, Button, Select, InputNumber, Input, DatePicker, message,
  Spin, Avatar, Empty, Popconfirm, Space, Divider, Form, Row, Col, Tooltip,
} from 'antd';
import {
  ClockCircleOutlined, CheckCircleOutlined, UserOutlined, DollarOutlined,
  TeamOutlined, PlusOutlined, DeleteOutlined, PhoneOutlined, InfoCircleOutlined,
  WalletOutlined, MinusCircleOutlined, EditOutlined, SaveOutlined, CloseOutlined,
  CalendarOutlined, ShoppingOutlined, FileDoneOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../../api/axios';
import OrderContractModal from './OrderContractModal';
import './OrderDetailModal.css';

const formatPrice = (p) => {
  if (!p && p !== 0) return '0';
  return Number(p).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

export const STATUS_MAP = {
  pending:     { color: 'orange',   label: 'Kutilmoqda',     icon: <ClockCircleOutlined /> },
  confirmed:   { color: 'blue',     label: 'Tasdiqlangan',   icon: <InfoCircleOutlined /> },
  in_progress: { color: 'geekblue', label: 'Jarayonda',      icon: <ClockCircleOutlined /> },
  completed:   { color: 'green',    label: 'Bajarildi',      icon: <CheckCircleOutlined /> },
  cancelled:   { color: 'red',      label: 'Bekor qilingan', icon: <MinusCircleOutlined /> },
  rejected:    { color: 'volcano',  label: 'Rad etilgan',    icon: <MinusCircleOutlined /> },
};

const hourOptions = Array.from({ length: 19 }, (_, i) => i + 6).map(h => ({ value: h, label: `${h}:00` }));

export default function OrderDetailModal({ open, orderId, onClose, onChanged, employees = [] }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const [contractOpen, setContractOpen] = useState(false);
  const [editForm] = Form.useForm();

  // Management inputs
  const [selEmpId, setSelEmpId] = useState(null);
  const [empPay, setEmpPay] = useState(0);
  const [expNote, setExpNote] = useState('');
  const [expSum, setExpSum] = useState(0);
  const [paySum, setPaySum] = useState(0);
  const [payType, setPayType] = useState('naqd');

  const fetchDetail = useCallback(() => {
    if (!orderId) return;
    setLoading(true);
    api.get(`/orders/${orderId}`)
      .then(res => setDetail(res.data?.data || null))
      .catch(() => message.error('Buyurtma tafsilotlari yuklanmadi'))
      .finally(() => setLoading(false));
  }, [orderId]);

  useEffect(() => {
    if (open && orderId) { setEditing(false); setActiveTab('info'); fetchDetail(); }
  }, [open, orderId, fetchDetail]);

  const refresh = () => { fetchDetail(); onChanged?.(); };

  // ─── Status change ───
  const changeStatus = (val) => {
    api.put(`/orders/${detail.id}/status`, { status: val })
      .then(() => { setDetail(p => ({ ...p, status: val })); onChanged?.(); message.success('Holat yangilandi'); })
      .catch(() => message.error('Holatni o\'zgartib bo\'lmadi'));
  };

  // ─── Edit order ───
  const startEdit = () => {
    editForm.setFieldsValue({
      date: detail.year && detail.month && detail.day
        ? dayjs(`${detail.year}-${String(detail.month).padStart(2, '0')}-${String(detail.day).padStart(2, '0')}`)
        : null,
      start_h: detail.start ? Number(String(detail.start).slice(0, 2)) : null,
      end_h: detail.end ? Number(String(detail.end).slice(0, 2)) : null,
      quantity: detail.quantity || 1,
      summa: Number(detail.summa) || 0,
      note: detail.note || '',
      client_name: detail.manual_client_name || '',
      client_phone: detail.manual_client_name ? (detail.client_phone || '') : '',
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    let v;
    try { v = await editForm.validateFields(); } catch { return; }
    const d = v.date ? dayjs(v.date) : null;
    const payload = {
      day: d ? d.date() : detail.day,
      month: d ? d.month() + 1 : detail.month,
      year: d ? d.year() : detail.year,
      start: v.start_h != null ? `${String(v.start_h).padStart(2, '0')}:00:00` : detail.start,
      end: v.end_h != null ? `${String(v.end_h).padStart(2, '0')}:00:00` : detail.end,
      quantity: v.quantity || 1,
      summa: v.summa,
      note: v.note || null,
      extra_id: detail.extra_id ?? null,
      client_name: detail.manual_client_name ? (v.client_name || null) : null,
      client_phone: detail.manual_client_name ? (v.client_phone || null) : null,
    };
    api.put(`/orders/${detail.id}`, payload)
      .then(() => { message.success('Buyurtma yangilandi'); setEditing(false); refresh(); })
      .catch(err => message.error(err.response?.data?.message || 'Saqlashda xatolik'));
  };

  // ─── Delete ───
  const deleteOrder = () => {
    api.delete(`/orders/${detail.id}`)
      .then(() => { message.success('Buyurtma o\'chirildi'); onChanged?.(); onClose(); })
      .catch(() => message.error('O\'chirishda xatolik'));
  };

  // ─── Employees ───
  const assignEmployee = () => {
    if (!selEmpId) return;
    api.post(`/orders/${detail.id}/employees`, { employees_id: selEmpId, pay_amount: empPay })
      .then(() => { setSelEmpId(null); setEmpPay(0); fetchDetail(); message.success('Xodim biriktirildi'); })
      .catch(() => message.error('Xatolik'));
  };
  const removeEmployee = (esId) => {
    api.delete(`/orders/${detail.id}/employees/${esId}`)
      .then(() => { fetchDetail(); message.success('Xodim olib tashlandi'); })
      .catch(() => message.error('Xatolik'));
  };

  // ─── Expenses ───
  const addExpense = () => {
    if (!expSum) return;
    api.post(`/orders/${detail.id}/expenses`, { note: expNote, summa: expSum })
      .then(() => { setExpNote(''); setExpSum(0); fetchDetail(); message.success('Xarajat qo\'shildi'); })
      .catch(() => message.error('Xatolik'));
  };
  const removeExpense = (id) => {
    api.delete(`/orders/${detail.id}/expenses/${id}`)
      .then(() => { fetchDetail(); message.success('Xarajat o\'chirildi'); })
      .catch(() => message.error('Xatolik'));
  };

  // ─── Payments ───
  const addPayment = () => {
    if (!paySum) return;
    api.post(`/orders/${detail.id}/payments`, { summa: paySum, paytype: payType })
      .then(() => { setPaySum(0); refresh(); message.success('To\'lov qabul qilindi'); })
      .catch(() => message.error('Xatolik'));
  };
  const removePayment = (id) => {
    api.delete(`/orders/${detail.id}/payments/${id}`)
      .then(() => { refresh(); message.success('To\'lov o\'chirildi'); })
      .catch(() => message.error('Xatolik'));
  };

  const st = detail ? (STATUS_MAP[detail.status] || STATUS_MAP.pending) : STATUS_MAP.pending;
  const total = Number(detail?.summa || 0);
  const paid = Number(detail?.payments_total || 0);
  const expenses = Number(detail?.expenses_total || 0);
  const remaining = Math.max(0, total - paid);
  const profit = paid - expenses;

  // ─── Hero header ───
  const renderHero = () => (
    <div className="odm-hero">
      <div className="odm-hero-top">
        <div>
          <div className="odm-hero-id">Buyurtma #{detail.id}
            {detail.source === 'manual' && <Tag className="odm-source-tag" bordered={false}>Qo'lda</Tag>}
          </div>
          <div className="odm-hero-sub">
            <ShoppingOutlined /> {detail.service_name || '—'}
            <span className="odm-dot">•</span>
            <CalendarOutlined /> {detail.day}/{detail.month}/{detail.year}
            {detail.start && <><span className="odm-dot">•</span><ClockCircleOutlined /> {String(detail.start).slice(0, 5)}—{String(detail.end).slice(0, 5)}</>}
          </div>
        </div>
        <Select
          value={detail.status}
          onChange={changeStatus}
          size="small"
          className="odm-status-select"
          popupMatchSelectWidth={false}
          options={Object.entries(STATUS_MAP).map(([k, v]) => ({ label: v.label, value: k }))}
        />
      </div>
      <div className="odm-hero-stats">
        <div className="odm-stat">
          <span className="odm-stat-label">Jami summa</span>
          <span className="odm-stat-val">{formatPrice(total)}</span>
        </div>
        <div className="odm-stat">
          <span className="odm-stat-label">To'langan</span>
          <span className="odm-stat-val odm-green">{formatPrice(paid)}</span>
        </div>
        <div className="odm-stat">
          <span className="odm-stat-label">Qoldiq</span>
          <span className={`odm-stat-val ${remaining > 0 ? 'odm-amber' : 'odm-green'}`}>{formatPrice(remaining)}</span>
        </div>
      </div>
    </div>
  );

  // ─── Tab: Ma'lumot ───
  const renderInfoTab = () => (
    <div className="odm-section-wrap">
      {!editing ? (
        <>
          <div className="odm-card">
            <div className="odm-card-head">
              <span><InfoCircleOutlined /> Buyurtma ma'lumotlari</span>
              <Button size="small" type="text" icon={<EditOutlined />} onClick={startEdit}>Tahrirlash</Button>
            </div>
            <div className="odm-info-grid">
              <div><span className="odm-k">Xizmat</span><span className="odm-v">{detail.service_name || '—'}</span></div>
              <div><span className="odm-k">Sana</span><span className="odm-v">{detail.day}/{detail.month}/{detail.year}</span></div>
              <div><span className="odm-k">Vaqt</span><span className="odm-v">{detail.start ? `${String(detail.start).slice(0, 5)} — ${String(detail.end).slice(0, 5)}` : '—'}</span></div>
              <div><span className="odm-k">Soni / mehmon</span><span className="odm-v">{detail.quantity || 1}</span></div>
              <div className="odm-info-full"><span className="odm-k">Izoh</span><span className="odm-v">{detail.note || '—'}</span></div>
            </div>
          </div>

          <div className="odm-card">
            <div className="odm-card-head"><span><UserOutlined /> Mijoz</span></div>
            <div className="odm-client">
              <Avatar size={44} icon={<UserOutlined />} style={{ background: '#667eea' }} />
              <div className="odm-client-info">
                <span className="odm-client-name">{detail.client_name || 'Noma\'lum mijoz'}</span>
                {detail.client_phone
                  ? <a className="odm-client-phone" href={`tel:${detail.client_phone}`}><PhoneOutlined /> {detail.client_phone}</a>
                  : <span className="odm-client-phone odm-muted">Telefon yo'q</span>}
              </div>
              {detail.client_phone && (
                <Tooltip title="Qo'ng'iroq qilish">
                  <Button shape="circle" icon={<PhoneOutlined />} href={`tel:${detail.client_phone}`} />
                </Tooltip>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="odm-card">
          <div className="odm-card-head"><span><EditOutlined /> Buyurtmani tahrirlash</span></div>
          <Form form={editForm} layout="vertical" requiredMark={false}>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="date" label="Sana" rules={[{ required: true, message: 'Sana' }]}>
                  <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="start_h" label="Boshlanish"><Select options={hourOptions} placeholder="—" /></Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="end_h" label="Tugash" rules={[({ getFieldValue }) => ({
                  validator(_, v) {
                    const s = getFieldValue('start_h');
                    if (v == null || s == null || v > s) return Promise.resolve();
                    return Promise.reject(new Error('Tugash > boshlanish'));
                  },
                })]}><Select options={hourOptions} placeholder="—" /></Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="quantity" label="Soni / mehmon"><InputNumber min={1} style={{ width: '100%' }} /></Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="summa" label="Narx (so'm)" rules={[{ required: true, message: 'Narx' }]}>
                  <InputNumber min={0} style={{ width: '100%' }}
                    formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                    parser={v => v.replace(/\s/g, '')} />
                </Form.Item>
              </Col>
            </Row>
            {detail.manual_client_name && (
              <Row gutter={12}>
                <Col span={12}><Form.Item name="client_name" label="Mijoz ismi"><Input prefix={<UserOutlined />} /></Form.Item></Col>
                <Col span={12}><Form.Item name="client_phone" label="Telefon"><Input prefix={<PhoneOutlined />} /></Form.Item></Col>
              </Row>
            )}
            <Form.Item name="note" label="Izoh"><Input.TextArea rows={2} /></Form.Item>
            <Space>
              <Button type="primary" icon={<SaveOutlined />} onClick={saveEdit}>Saqlash</Button>
              <Button icon={<CloseOutlined />} onClick={() => setEditing(false)}>Bekor</Button>
            </Space>
          </Form>
        </div>
      )}
    </div>
  );

  // ─── Tab: Moliya ───
  const renderFinanceTab = () => (
    <div className="odm-section-wrap">
      <div className="odm-fin-summary">
        <div className="odm-fin-item"><span>Buyurtma summasi</span><b>{formatPrice(total)} so'm</b></div>
        <div className="odm-fin-item"><span>To'langan</span><b className="odm-green">{formatPrice(paid)} so'm</b></div>
        <div className="odm-fin-item"><span>Qoldiq</span><b className={remaining > 0 ? 'odm-amber' : 'odm-green'}>{formatPrice(remaining)} so'm</b></div>
        <div className="odm-fin-item"><span>Xarajatlar</span><b className="odm-red">{formatPrice(expenses)} so'm</b></div>
        <div className="odm-fin-item odm-fin-profit"><span>Sof foyda</span><b>{formatPrice(profit)} so'm</b></div>
      </div>

      {/* To'lovlar */}
      <div className="odm-card">
        <div className="odm-card-head"><span><DollarOutlined /> To'lovlar ({formatPrice(paid)} so'm)</span></div>
        {(detail.payments || []).map(pay => (
          <div key={pay.id} className="odm-row">
            <div><Tag color="green" style={{ fontSize: 10 }}>{pay.paytype}</Tag>
              <span className="odm-muted" style={{ fontSize: 11 }}>{pay.date ? String(pay.date).slice(0, 10) : ''}</span></div>
            <Space size={8}>
              <span className="odm-green">+{formatPrice(pay.summa)} so'm</span>
              <Popconfirm title="O'chirish?" onConfirm={() => removePayment(pay.id)} okText="Ha" cancelText="Yo'q">
                <Button type="text" danger size="small" icon={<DeleteOutlined />} />
              </Popconfirm>
            </Space>
          </div>
        ))}
        <div className="odm-add-row">
          <Select value={payType} onChange={setPayType} style={{ width: 110 }}
            options={[{ label: 'Naqd', value: 'naqd' }, { label: 'Karta', value: 'karta' }, { label: 'Bank', value: 'bank' }]} />
          <InputNumber placeholder="Summa" value={paySum} onChange={setPaySum} min={0} style={{ flex: 1 }}
            formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} parser={v => v.replace(/\s/g, '')} />
          <Button type="primary" className="odm-green-btn" icon={<PlusOutlined />} onClick={addPayment} disabled={!paySum} />
        </div>
      </div>

      {/* Xarajatlar */}
      <div className="odm-card">
        <div className="odm-card-head"><span><WalletOutlined /> Xarajatlar ({formatPrice(expenses)} so'm)</span></div>
        {(detail.expenses || []).map(exp => (
          <div key={exp.id} className="odm-row">
            <span style={{ fontSize: 13 }}>{exp.note || 'Xarajat'}</span>
            <Space size={8}>
              <span className="odm-red">-{formatPrice(exp.summa)} so'm</span>
              <Popconfirm title="O'chirish?" onConfirm={() => removeExpense(exp.id)} okText="Ha" cancelText="Yo'q">
                <Button type="text" danger size="small" icon={<DeleteOutlined />} />
              </Popconfirm>
            </Space>
          </div>
        ))}
        <div className="odm-add-row">
          <Input placeholder="Izoh" value={expNote} onChange={e => setExpNote(e.target.value)} style={{ flex: 1 }} />
          <InputNumber placeholder="Summa" value={expSum} onChange={setExpSum} min={0} style={{ width: 130 }}
            formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} parser={v => v.replace(/\s/g, '')} />
          <Button type="primary" danger icon={<PlusOutlined />} onClick={addExpense} disabled={!expSum} />
        </div>
      </div>
    </div>
  );

  // ─── Tab: Xodimlar ───
  const renderEmployeesTab = () => (
    <div className="odm-section-wrap">
      <div className="odm-card">
        <div className="odm-card-head"><span><TeamOutlined /> Biriktirilgan xodimlar ({detail.employees?.length || 0})</span></div>
        {(detail.employees || []).length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Xodim biriktirilmagan" />}
        {(detail.employees || []).map(emp => (
          <div key={emp.id} className="odm-row">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Avatar size={26} icon={<UserOutlined />} />
              <span style={{ fontSize: 13 }}>{emp.fullname || '—'}</span>
              {emp.position_name && <Tag style={{ fontSize: 10 }}>{emp.position_name}</Tag>}
            </div>
            <Space size={8}>
              <span className="odm-muted">{formatPrice(emp.pay_amount)} so'm</span>
              <Popconfirm title="Olib tashlash?" onConfirm={() => removeEmployee(emp.id)} okText="Ha" cancelText="Yo'q">
                <Button type="text" danger size="small" icon={<DeleteOutlined />} />
              </Popconfirm>
            </Space>
          </div>
        ))}
        <div className="odm-add-row">
          <Select placeholder="Xodim tanlang" value={selEmpId} onChange={setSelEmpId} style={{ flex: 1 }}
            allowClear showSearch optionFilterProp="label"
            options={employees.map(e => ({ label: e.fullname || e.phone, value: e.id }))} />
          <InputNumber placeholder="To'lov" value={empPay} onChange={setEmpPay} min={0} style={{ width: 120 }}
            formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} parser={v => v.replace(/\s/g, '')} />
          <Button type="primary" icon={<PlusOutlined />} onClick={assignEmployee} disabled={!selEmpId} />
        </div>
      </div>
    </div>
  );

  return (
    <>
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
      title={null}
      closeIcon={null}
      destroyOnHidden
      className="odm-modal"
      styles={{ body: { padding: 0 } }}
    >
      {!detail ? (
        <div style={{ padding: 60, textAlign: 'center' }}><Spin size="large" /></div>
      ) : (
        <>
          {renderHero()}
          <div className="odm-actions">
            <Space size={8}>
              <Tag icon={st.icon} color={st.color} className="odm-status-chip">{st.label}</Tag>
              {loading && <Spin size="small" />}
            </Space>
            <Space>
              <Button size="small" icon={<FileDoneOutlined />} onClick={() => setContractOpen(true)}>Shartnoma</Button>
              {!editing && <Button size="small" icon={<EditOutlined />} onClick={startEdit}>Tahrirlash</Button>}
              <Popconfirm title="Buyurtmani o'chirish?" description="Bu amalni qaytarib bo'lmaydi." onConfirm={deleteOrder} okText="O'chirish" okButtonProps={{ danger: true }} cancelText="Bekor">
                <Button size="small" danger icon={<DeleteOutlined />}>O'chirish</Button>
              </Popconfirm>
              <Button size="small" type="text" icon={<CloseOutlined />} onClick={onClose} />
            </Space>
          </div>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            className="odm-tabs"
            items={[
              { key: 'info', label: <span><InfoCircleOutlined /> Ma'lumot</span>, children: renderInfoTab() },
              { key: 'finance', label: <span><DollarOutlined /> Moliya</span>, children: renderFinanceTab() },
              { key: 'employees', label: <span><TeamOutlined /> Xodimlar</span>, children: renderEmployeesTab() },
            ]}
          />
        </>
      )}
    </Modal>
    <OrderContractModal open={contractOpen} order={detail} onClose={() => setContractOpen(false)} />
    </>
  );
}
